import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Save, Upload, Plus, Trash2, Search, Star, Image as ImageIcon, ChevronDown, ChevronUp, Globe } from 'lucide-react';
import { toast } from 'sonner';
import DarkSelect from './DarkSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image URLs - /uploads/* paths are served from backend
const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  // If it's already a full URL (http/https), return as is
  if (imagePath.startsWith('http')) return imagePath;
  // If it's an uploads path, prepend the API URL
  if (imagePath.startsWith('/uploads/')) {
    return `${API_URL}/api${imagePath}`;
  }
  // Otherwise return as is (for legacy local paths)
  return imagePath;
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const PlayerFormModal = ({ 
  isOpen, 
  onClose, 
  editingPlayer, 
  onSave,
  getToken 
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [uploading, setUploading] = useState({});
  const [clubSearchQuery, setClubSearchQuery] = useState('');
  const [clubSearchResults, setClubSearchResults] = useState([]);
  const [searchingClubs, setSearchingClubs] = useState(false);
  
  const fileInputRefs = {
    image: useRef(null),
    image2: useRef(null),
    gallery: useRef(null),
    clubLogo: useRef(null)
  };

  const [formData, setFormData] = useState({
    name: '',
    position: '',
    club: '',
    country: '',
    secondary_nationality: '',
    birth_date: '',
    category: '',
    image: '',
    image2: '',
    gallery: [],
    instagram: '',
    twitter: '',
    tiktok: '',
    facebook: '',
    biography: '',
    height: '',
    weight: '',
    dominant_foot: '',
    shirt_number: '',
    club_history: [],
    national_team_history: [],
    achievements: [],
    video_url: '',
    is_featured: false,
    show_in_home: false,
    display_order: 0,
    has_page: false,
    slug: ''
  });

  // Track home players count
  const [homePlayersCount, setHomePlayersCount] = useState(0);
  const MAX_HOME_PLAYERS = 5;

  // New club entry being edited
  const [newClub, setNewClub] = useState({
    club_name: '',
    club_logo: '',
    year_start: '',
    year_end: '',
    is_abroad: false
  });

  // New national team entry being edited
  const [newNationalTeam, setNewNationalTeam] = useState({
    team_type: 'Mayor',
    is_friendly: false,
    year: '',
    description: ''
  });

  // New achievement being edited
  const [newAchievement, setNewAchievement] = useState('');

  useEffect(() => {
    // Fetch home players count
    const fetchHomeCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/players/home-count`);
        if (response.ok) {
          const data = await response.json();
          setHomePlayersCount(data.count);
        }
      } catch (err) {
        console.error('Error fetching home count:', err);
      }
    };
    
    if (isOpen) {
      fetchHomeCount();
    }
  }, [isOpen]);

  useEffect(() => {
    if (editingPlayer) {
      setFormData({
        name: editingPlayer.name || '',
        position: editingPlayer.position || '',
        club: editingPlayer.club || '',
        country: editingPlayer.country || '',
        secondary_nationality: editingPlayer.secondary_nationality || '',
        birth_date: editingPlayer.birth_date || '',
        category: editingPlayer.category || '',
        image: editingPlayer.image || '',
        image2: editingPlayer.image2 || '',
        gallery: editingPlayer.gallery || [],
        instagram: editingPlayer.instagram || '',
        twitter: editingPlayer.twitter || '',
        tiktok: editingPlayer.tiktok || '',
        facebook: editingPlayer.facebook || '',
        biography: editingPlayer.biography || '',
        height: editingPlayer.height || '',
        weight: editingPlayer.weight || '',
        dominant_foot: editingPlayer.dominant_foot || '',
        shirt_number: editingPlayer.shirt_number || '',
        club_history: editingPlayer.club_history || [],
        national_team_history: editingPlayer.national_team_history || [],
        achievements: editingPlayer.achievements || [],
        video_url: editingPlayer.video_url || '',
        is_featured: editingPlayer.is_featured || false,
        show_in_home: editingPlayer.show_in_home || false,
        display_order: editingPlayer.display_order || 0,
        has_page: editingPlayer.has_page || false,
        slug: editingPlayer.slug || ''
      });
    } else {
      // Reset form for new player
      setFormData({
        name: '',
        position: '',
        club: '',
        country: '',
        secondary_nationality: '',
        birth_date: '',
        category: '',
        image: '',
        image2: '',
        gallery: [],
        instagram: '',
        twitter: '',
        tiktok: '',
        facebook: '',
        biography: '',
        height: '',
        weight: '',
        dominant_foot: '',
        shirt_number: '',
        club_history: [],
        national_team_history: [],
        achievements: [],
        video_url: '',
        is_featured: false,
        show_in_home: false,
        display_order: 0,
        has_page: false,
        slug: ''
      });
    }
    setActiveTab('basic');
  }, [editingPlayer, isOpen]);

  const handleFileUpload = async (file, field) => {
    setUploading(prev => ({ ...prev, [field]: true }));
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formDataUpload
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al subir imagen');
      }
      
      const data = await response.json();
      
      if (field === 'gallery') {
        setFormData(prev => ({ 
          ...prev, 
          gallery: [...(prev.gallery || []), data.url] 
        }));
      } else if (field === 'clubLogo') {
        setNewClub(prev => ({ ...prev, club_logo: data.url }));
      } else {
        setFormData(prev => ({ ...prev, [field]: data.url }));
      }
      
      toast.success('Imagen subida');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  // Debounced search query
  const debouncedSearchQuery = useDebounce(clubSearchQuery, 400);
  
  // Search clubs when debounced query changes
  useEffect(() => {
    const searchClubs = async () => {
      if (debouncedSearchQuery.length < 2) {
        setClubSearchResults([]);
        return;
      }
      
      setSearchingClubs(true);
      try {
        const response = await fetch(`${API_URL}/api/clubs/search?query=${encodeURIComponent(debouncedSearchQuery)}`);
        if (response.ok) {
          const data = await response.json();
          setClubSearchResults(data.clubs || []);
        }
      } catch (err) {
        console.error('Club search error:', err);
      } finally {
        setSearchingClubs(false);
      }
    };
    
    searchClubs();
  }, [debouncedSearchQuery]);

  const selectClub = (club) => {
    setNewClub(prev => ({
      ...prev,
      club_name: club.name,
      club_logo: club.logo || ''
    }));
    setClubSearchQuery('');
    setClubSearchResults([]);
  };

  const addClubToHistory = () => {
    if (!newClub.club_name) return;
    
    setFormData(prev => ({
      ...prev,
      club_history: [...(prev.club_history || []), { ...newClub }]
    }));
    
    setNewClub({ club_name: '', club_logo: '', year_start: '', year_end: '', is_abroad: false });
  };

  const removeClubFromHistory = (index) => {
    setFormData(prev => ({
      ...prev,
      club_history: prev.club_history.filter((_, i) => i !== index)
    }));
  };

  const addAchievement = () => {
    if (!newAchievement.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      achievements: [...(prev.achievements || []), newAchievement.trim()]
    }));
    
    setNewAchievement('');
  };

  const removeAchievement = (index) => {
    setFormData(prev => ({
      ...prev,
      achievements: prev.achievements.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      age: formData.age ? parseInt(formData.age) : null,
      shirt_number: formData.shirt_number ? parseInt(formData.shirt_number) : null,
      club: formData.club || null,
      secondary_nationality: formData.secondary_nationality || null,
      image: formData.image || null,
      image2: formData.image2 || null,
      gallery: formData.gallery || [],
      instagram: formData.instagram || null,
      twitter: formData.twitter || null,
      tiktok: formData.tiktok || null,
      facebook: formData.facebook || null,
      biography: formData.biography || null,
      height: formData.height || null,
      weight: formData.weight || null,
      dominant_foot: formData.dominant_foot || null,
      club_history: formData.club_history || [],
      achievements: formData.achievements || [],
      video_url: formData.video_url || null,
      slug: formData.slug || null
    };

    await onSave(payload);
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'basic', label: 'Información Básica' },
    { id: 'images', label: 'Imágenes' },
    { id: 'career', label: 'Trayectoria' },
    { id: 'extra', label: 'Info Extra' },
    { id: 'page', label: 'Página Individual' }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-medium text-white">
            {editingPlayer ? 'Editar Jugador' : 'Nuevo Jugador'}
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'text-white border-b-2 border-white'
                  : 'text-white/50 hover:text-white/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Nombre completo"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2">Posición *</label>
                  <DarkSelect
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                    placeholder="Seleccionar"
                    options={[
                      { value: 'Portero', label: 'Portero' },
                      { value: 'Defensa', label: 'Defensa' },
                      { value: 'Lateral', label: 'Lateral' },
                      { value: 'Mediocampista', label: 'Mediocampista' },
                      { value: 'Extremo', label: 'Extremo' },
                      { value: 'Delantero', label: 'Delantero' },
                    ]}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Club actual</label>
                  <input
                    type="text"
                    value={formData.club}
                    onChange={(e) => setFormData({...formData, club: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Club actual"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2">País *</label>
                  <input
                    type="text"
                    required
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Nacionalidad"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Segunda nacionalidad</label>
                  <input
                    type="text"
                    value={formData.secondary_nationality}
                    onChange={(e) => setFormData({...formData, secondary_nationality: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2">Fecha de nacimiento</label>
                  <input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => {
                      const birthDate = e.target.value;
                      const year = birthDate ? birthDate.split('-')[0] : '';
                      setFormData({
                        ...formData, 
                        birth_date: birthDate,
                        category: formData.category || year // Auto-suggest but don't override
                      });
                    }}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none [color-scheme:dark]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Categoría (año)</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Ej: 2008, 2009..."
                  />
                  <p className="text-white/40 text-xs mt-1">Se sugiere automáticamente del año de nacimiento</p>
                </div>
              </div>

              {/* Redes Sociales */}
              <div className="pt-4 border-t border-white/10">
                <h3 className="text-white font-medium mb-4">Redes Sociales</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-white/80 text-sm mb-2">Instagram</label>
                    <input
                      type="url"
                      value={formData.instagram}
                      onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm mb-2">Twitter / X</label>
                    <input
                      type="url"
                      value={formData.twitter}
                      onChange={(e) => setFormData({...formData, twitter: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm mb-2">TikTok</label>
                    <input
                      type="url"
                      value={formData.tiktok}
                      onChange={(e) => setFormData({...formData, tiktok: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="https://tiktok.com/@..."
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm mb-2">Facebook</label>
                    <input
                      type="url"
                      value={formData.facebook}
                      onChange={(e) => setFormData({...formData, facebook: e.target.value})}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                </div>
              </div>

              {/* Flags */}
              <div className="pt-4 border-t border-white/10 space-y-4">
                <h3 className="text-white font-medium">Visibilidad</h3>
                
                {/* Destacado */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={formData.is_featured}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData({
                        ...formData, 
                        is_featured: isChecked,
                        // If removing featured, also remove from home
                        show_in_home: isChecked ? formData.show_in_home : false
                      });
                    }}
                    className="w-5 h-5 rounded bg-white/10 border-white/20 accent-yellow-500"
                  />
                  <label htmlFor="is_featured" className="text-white font-light flex items-center gap-2">
                    <Star size={18} className={formData.is_featured ? 'text-yellow-500 fill-yellow-500' : 'text-white/30'} />
                    Jugador destacado
                  </label>
                </div>
                
                {/* Home */}
                <div className="flex items-center gap-3 ml-6">
                  <input
                    type="checkbox"
                    id="show_in_home"
                    checked={formData.show_in_home}
                    disabled={!formData.is_featured || (homePlayersCount >= MAX_HOME_PLAYERS && !editingPlayer?.show_in_home)}
                    onChange={(e) => setFormData({...formData, show_in_home: e.target.checked})}
                    className="w-5 h-5 rounded bg-white/10 border-white/20 accent-yellow-500 disabled:opacity-30"
                  />
                  <label htmlFor="show_in_home" className={`font-light flex items-center gap-2 ${!formData.is_featured ? 'text-white/30' : 'text-white'}`}>
                    Mostrar en Home
                    <span className={`text-xs px-2 py-0.5 rounded-full ${homePlayersCount >= MAX_HOME_PLAYERS ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/50'}`}>
                      {homePlayersCount}/{MAX_HOME_PLAYERS}
                    </span>
                  </label>
                </div>
                {!formData.is_featured && (
                  <p className="text-white/40 text-xs ml-6">Debe ser destacado para aparecer en Home</p>
                )}
                {homePlayersCount >= MAX_HOME_PLAYERS && !editingPlayer?.show_in_home && formData.is_featured && (
                  <p className="text-amber-400 text-xs ml-6">Máximo alcanzado. Quita otro jugador de Home primero.</p>
                )}
                
                {/* Display Order */}
                {formData.is_featured && (
                  <div className="ml-6">
                    <label className="block text-white/60 text-sm mb-2">Orden de visualización</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.display_order}
                      onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                      className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-white/30 outline-none"
                      placeholder="0"
                    />
                    <p className="text-white/40 text-xs mt-1">Menor número = aparece primero</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div className="space-y-6">
              {/* Main Image */}
              <div>
                <label className="block text-white/80 text-sm mb-3">Imagen principal</label>
                <div className="flex gap-4 items-start">
                  <div className="w-24 h-28 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
                    {formData.image ? (
                      <img src={resolveImageUrl(formData.image)} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">
                        <ImageIcon size={32} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input type="file" ref={fileInputRefs.image} accept="image/*" className="hidden"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'image')} />
                    <button type="button" onClick={() => fileInputRefs.image.current?.click()}
                      disabled={uploading.image}
                      className="w-full px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                      {uploading.image ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
                      <span>{uploading.image ? 'Subiendo...' : 'Subir foto'}</span>
                    </button>
                    <input type="text" value={formData.image}
                      onChange={(e) => setFormData({...formData, image: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="O pegar URL..." />
                  </div>
                </div>
              </div>

              {/* Secondary Image */}
              <div>
                <label className="block text-white/80 text-sm mb-3">Imagen secundaria</label>
                <div className="flex gap-4 items-start">
                  <div className="w-24 h-28 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
                    {formData.image2 ? (
                      <img src={resolveImageUrl(formData.image2)} alt="Preview 2" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">
                        <ImageIcon size={32} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input type="file" ref={fileInputRefs.image2} accept="image/*" className="hidden"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'image2')} />
                    <button type="button" onClick={() => fileInputRefs.image2.current?.click()}
                      disabled={uploading.image2}
                      className="w-full px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg text-white text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                      {uploading.image2 ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
                      <span>{uploading.image2 ? 'Subiendo...' : 'Subir foto'}</span>
                    </button>
                    <input type="text" value={formData.image2}
                      onChange={(e) => setFormData({...formData, image2: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="O pegar URL..." />
                  </div>
                </div>
              </div>

              {/* Gallery */}
              <div>
                <label className="block text-white/80 text-sm mb-3">Galería de fotos</label>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {(formData.gallery || []).map((img, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-white/5 group">
                      <img src={resolveImageUrl(img)} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeGalleryImage(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => fileInputRefs.gallery.current?.click()}
                    disabled={uploading.gallery}
                    className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center text-white/40 hover:text-white/60 hover:border-white/40 transition-colors">
                    {uploading.gallery ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Plus size={24} />}
                  </button>
                </div>
                <input type="file" ref={fileInputRefs.gallery} accept="image/*" className="hidden"
                  onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'gallery')} />
              </div>

              {/* Video */}
              <div>
                <label className="block text-white/80 text-sm mb-2">Video destacado (YouTube)</label>
                <input type="url" value={formData.video_url}
                  onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                  placeholder="https://youtube.com/watch?v=..." />
              </div>
            </div>
          )}

          {/* Career History Tab */}
          {activeTab === 'career' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-white font-medium mb-4">Historial de Clubes</h3>
                
                {/* Existing clubs */}
                <div className="space-y-3 mb-6">
                  {(formData.club_history || []).map((club, index) => (
                    <div key={index} className="flex items-center gap-4 bg-white/5 rounded-xl p-3 border border-white/10">
                      <div className="w-12 h-12 rounded-lg bg-white/10 overflow-hidden flex items-center justify-center">
                        {club.club_logo ? (
                          <img src={resolveImageUrl(club.club_logo)} alt={club.club_name} className="w-10 h-10 object-contain" />
                        ) : (
                          <span className="text-white/30 font-bold">{club.club_name?.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{club.club_name}</p>
                          {club.is_abroad && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded-full flex items-center gap-1">
                              <Globe size={10} />
                              Extranjero
                            </span>
                          )}
                        </div>
                        <p className="text-white/50 text-sm">{club.year_start || '?'} - {club.year_end || 'Presente'}</p>
                      </div>
                      <button type="button" onClick={() => removeClubFromHistory(index)}
                        className="p-2 text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new club */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h4 className="text-white/80 text-sm mb-3">Agregar club</h4>
                  
                  {/* Club search */}
                  <div className="relative mb-3">
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                        <input type="text" value={clubSearchQuery}
                          onChange={(e) => setClubSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                          placeholder="Buscar club (ej: Peñarol, Gremio)..." />
                        {searchingClubs && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Search results dropdown */}
                    {clubSearchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-neutral-900 border border-white/10 rounded-lg overflow-hidden z-10 max-h-60 overflow-y-auto">
                        {clubSearchResults.map((club, index) => (
                          <button key={index} type="button" onClick={() => selectClub(club)}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/10 text-left">
                            {club.logo && (
                              <img src={club.logo} alt={club.name} className="w-8 h-8 object-contain" />
                            )}
                            <div>
                              <p className="text-white text-sm">{club.name}</p>
                              <p className="text-white/40 text-xs">{club.country} - {club.league}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Manual entry */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input type="text" value={newClub.club_name}
                      onChange={(e) => setNewClub({...newClub, club_name: e.target.value})}
                      className="col-span-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="Nombre del club" />
                    <input type="text" value={newClub.year_start}
                      onChange={(e) => setNewClub({...newClub, year_start: e.target.value})}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="Año inicio" />
                    <input type="text" value={newClub.year_end}
                      onChange={(e) => setNewClub({...newClub, year_end: e.target.value})}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="Año fin (vacío = presente)" />
                  </div>
                  
                  {/* Is abroad checkbox */}
                  <div className="flex items-center gap-3 mb-3 px-1">
                    <input
                      type="checkbox"
                      id="is_abroad"
                      checked={newClub.is_abroad}
                      onChange={(e) => setNewClub({...newClub, is_abroad: e.target.checked})}
                      className="w-4 h-4 rounded bg-white/10 border-white/20 accent-blue-500"
                    />
                    <label htmlFor="is_abroad" className="text-white/80 text-sm flex items-center gap-2 cursor-pointer">
                      <Globe size={14} className={newClub.is_abroad ? 'text-blue-400' : 'text-white/40'} />
                      Club extranjero
                    </label>
                  </div>
                  
                  {/* Club logo upload */}
                  <div className="flex gap-3 items-center mb-3">
                    <div className="w-12 h-12 rounded-lg bg-white/10 overflow-hidden flex items-center justify-center">
                      {newClub.club_logo ? (
                        <img src={resolveImageUrl(newClub.club_logo)} alt="Logo" className="w-10 h-10 object-contain" />
                      ) : (
                        <ImageIcon size={20} className="text-white/30" />
                      )}
                    </div>
                    <input type="file" ref={fileInputRefs.clubLogo} accept="image/*" className="hidden"
                      onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'clubLogo')} />
                    <button type="button" onClick={() => fileInputRefs.clubLogo.current?.click()}
                      disabled={uploading.clubLogo}
                      className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-xs flex items-center gap-2 transition-colors">
                      {uploading.clubLogo ? <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> : <Upload size={14} />}
                      Subir escudo
                    </button>
                    <span className="text-white/30 text-xs">o</span>
                    <input type="text" value={newClub.club_logo}
                      onChange={(e) => setNewClub({...newClub, club_logo: e.target.value})}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-xs placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="URL del escudo" />
                  </div>

                  <button type="button" onClick={addClubToHistory}
                    disabled={!newClub.club_name}
                    className="w-full py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    Agregar club
                  </button>
                </div>
              </div>

              {/* National Team History */}
              <div>
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <span className="text-xl">🇺🇾</span>
                  Selección Nacional
                </h3>
                
                {/* Existing national team entries */}
                <div className="space-y-3 mb-6">
                  {(formData.national_team_history || []).map((entry, index) => (
                    <div key={index} className="flex items-center gap-4 bg-white/5 rounded-xl p-3 border border-white/10">
                      <div className="w-12 h-12 rounded-lg bg-sky-500/20 overflow-hidden flex items-center justify-center text-xl">
                        🇺🇾
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{entry.team_type}</p>
                          {entry.is_friendly && (
                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-[10px] rounded-full">
                              Amistoso
                            </span>
                          )}
                        </div>
                        <p className="text-white/50 text-sm">
                          {entry.year || 'Sin año'}
                          {entry.description && ` - ${entry.description}`}
                        </p>
                      </div>
                      <button type="button" onClick={() => {
                        const updated = [...(formData.national_team_history || [])];
                        updated.splice(index, 1);
                        setFormData({...formData, national_team_history: updated});
                      }}
                        className="p-2 text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new national team entry */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <p className="text-white/60 text-sm mb-3">Agregar convocatoria</p>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-white/60 text-xs mb-1">Selección</label>
                      <DarkSelect
                        value={newNationalTeam.team_type}
                        onChange={(e) => setNewNationalTeam({...newNationalTeam, team_type: e.target.value})}
                        options={[
                          { value: 'Mayor', label: 'Mayor (Primera)' },
                          { value: 'Sub-23', label: 'Sub-23' },
                          { value: 'Sub-20', label: 'Sub-20' },
                          { value: 'Sub-17', label: 'Sub-17' },
                          { value: 'Sub-15', label: 'Sub-15' },
                        ]}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs mb-1">Año</label>
                      <input 
                        type="text" 
                        value={newNationalTeam.year}
                        onChange={(e) => setNewNationalTeam({...newNationalTeam, year: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                        placeholder="Ej: 2024" 
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="block text-white/60 text-xs mb-1">Descripción (opcional)</label>
                    <input 
                      type="text" 
                      value={newNationalTeam.description}
                      onChange={(e) => setNewNationalTeam({...newNationalTeam, description: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="Ej: Copa América 2024, Eliminatorias, etc." 
                    />
                  </div>
                  
                  <div className="flex items-center gap-3 mb-4 px-1">
                    <input
                      type="checkbox"
                      id="is_friendly"
                      checked={newNationalTeam.is_friendly}
                      onChange={(e) => setNewNationalTeam({...newNationalTeam, is_friendly: e.target.checked})}
                      className="w-4 h-4 rounded bg-white/10 border-white/20 accent-orange-500"
                    />
                    <label htmlFor="is_friendly" className="text-white/80 text-sm cursor-pointer">
                      Partido amistoso
                    </label>
                  </div>
                  
                  <button 
                    type="button"
                    onClick={() => {
                      if (newNationalTeam.team_type) {
                        setFormData({
                          ...formData,
                          national_team_history: [...(formData.national_team_history || []), {...newNationalTeam}]
                        });
                        setNewNationalTeam({ team_type: 'Mayor', is_friendly: false, year: '', description: '' });
                      }
                    }}
                    disabled={!newNationalTeam.team_type}
                    className="w-full py-2 bg-sky-500/20 hover:bg-sky-500/30 rounded-lg text-sky-400 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Agregar a selección
                  </button>
                </div>
              </div>

              {/* Achievements */}
              <div>
                <h3 className="text-white font-medium mb-4">Logros y Títulos</h3>
                
                <div className="space-y-2 mb-4">
                  {(formData.achievements || []).map((achievement, index) => (
                    <div key={index} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2 border border-white/10">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                      <span className="flex-1 text-white text-sm">{achievement}</span>
                      <button type="button" onClick={() => removeAchievement(index)}
                        className="text-red-400 hover:text-red-300">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input type="text" value={newAchievement}
                    onChange={(e) => setNewAchievement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAchievement())}
                    className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Ej: Campeón Uruguayo 2023" />
                  <button type="button" onClick={addAchievement}
                    disabled={!newAchievement.trim()}
                    className="px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors disabled:opacity-50">
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Extra Info Tab */}
          {activeTab === 'extra' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Altura</label>
                  <input type="text" value={formData.height}
                    onChange={(e) => setFormData({...formData, height: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Ej: 1.78m" />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2">Peso</label>
                  <input type="text" value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Ej: 73kg" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-white/80 text-sm mb-2">Pie dominante</label>
                  <DarkSelect
                    value={formData.dominant_foot}
                    onChange={(e) => setFormData({...formData, dominant_foot: e.target.value})}
                    placeholder="Seleccionar"
                    options={[
                      { value: 'Derecho', label: 'Derecho' },
                      { value: 'Izquierdo', label: 'Izquierdo' },
                      { value: 'Ambidiestro', label: 'Ambidiestro' },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm mb-2">Número de camiseta</label>
                  <input type="number" value={formData.shirt_number}
                    onChange={(e) => setFormData({...formData, shirt_number: e.target.value})}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                    placeholder="Ej: 10" />
                </div>
              </div>

              <div>
                <label className="block text-white/80 text-sm mb-2">Biografía</label>
                <textarea value={formData.biography}
                  onChange={(e) => setFormData({...formData, biography: e.target.value})}
                  rows={6}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none resize-none"
                  placeholder="Descripción del jugador, trayectoria, estilo de juego..." />
              </div>
            </div>
          )}

          {/* Page Tab */}
          {activeTab === 'page' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <input type="checkbox" id="has_page" checked={formData.has_page}
                    onChange={(e) => setFormData({...formData, has_page: e.target.checked})}
                    className="w-6 h-6 mt-1 rounded bg-white/10 border-white/20" />
                  <div>
                    <label htmlFor="has_page" className="text-white font-medium text-lg cursor-pointer">
                      Crear página individual
                    </label>
                    <p className="text-white/60 text-sm mt-1">
                      Genera una landing page para este jugador con toda su información, 
                      galería de fotos, trayectoria y redes sociales. Perfecta para compartir en Instagram.
                    </p>
                  </div>
                </div>
              </div>

              {formData.has_page && (
                <div>
                  <label className="block text-white/80 text-sm mb-2">URL personalizada</label>
                  <div className="flex items-center gap-2">
                    <span className="text-white/50">/jugadores/</span>
                    <input type="text" value={formData.slug}
                      onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-white/30 outline-none"
                      placeholder="matias-arezo" />
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    Si lo dejas vacío, se generará automáticamente a partir del nombre del jugador.
                  </p>
                </div>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-4 p-6 border-t border-white/10">
          <button type="button" onClick={onClose}
            className="px-6 py-3 text-white/60 hover:text-white transition-colors">
            Cancelar
          </button>
          <button onClick={handleSubmit}
            className="flex items-center gap-2 px-6 py-3 bg-white text-black font-medium rounded-xl hover:bg-white/90 transition-colors">
            <Save size={18} />
            <span>{editingPlayer ? 'Guardar cambios' : 'Crear jugador'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerFormModal;
